import React, { useState, useEffect } from 'react';
import { StudentProfile, StudentReferralStats } from '../../types';
import { referralService } from '../../services/referralService';
import {
  Copy,
  Check,
  Send,
  Users,
  Sparkles,
  CheckCircle2,
  Calendar,
} from 'lucide-react';
import './StudentPromoCard.css';

interface Props {
  studentProfile: StudentProfile | null;
}

const ARABIC_MONTH_NAMES: Record<string, string> = {
  '01': 'يناير',
  '02': 'فبراير',
  '03': 'مارس',
  '04': 'أبريل',
  '05': 'مايو',
  '06': 'يونيو',
  '07': 'يوليو',
  '08': 'أغسطس',
  '09': 'سبتمبر',
  '10': 'أكتوبر',
  '11': 'نوفمبر',
  '12': 'ديسمبر',
};

const formatArabicMonth = (yearMonth: string): string => {
  if (!yearMonth) return '';
  const [year, month] = yearMonth.split('-');
  const monthName = ARABIC_MONTH_NAMES[month] || month;
  return `${monthName} ${year}`;
};

export const StudentPromoCard: React.FC<Props> = ({ studentProfile }) => {
  const [referralCode, setReferralCode] = useState<string>('');
  const [stats, setStats] = useState<StudentReferralStats>({
    referralCode: '',
    totalInvited: 0,
    activeBookedCount: 0,
    earnedDiscountMonths: [],
  });
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!studentProfile) return;

    const loadReferralInfo = async () => {
      try {
        const code = await referralService.getOrCreateReferralCode(studentProfile);
        setReferralCode(code);

        const currentStats = await referralService.getReferralStats(studentProfile);
        setStats(currentStats);
      } catch (err) {
        console.warn('Error loading referral info:', err);
      }
    };

    loadReferralInfo();
  }, [studentProfile]);

  const handleCopyCode = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShareWhatsApp = () => {
    if (!referralCode) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://otostudents.com';
    const registerUrl = `${origin}/register?ref=${encodeURIComponent(referralCode)}`;

    const message = `السلام عليكم ورحمة الله وبركاته،
سجل معايا في منظومة OTO STUDENTS لنقل طلاب الجامعات من الإسكندرية مباشرة لباب كليتك 🚌🎓

استخدم كود الدعوة الخاص بي عند التسجيل:
🔑 كود البرومو: ${referralCode}

📲 رابط التسجيل المباشر:
${registerUrl}

رحلات آمنة ومريحة بحافلات سياحية 50 راكباً ومقاعد مضمونة! ✨`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  const activeBookings = stats.activeBookedCount || 0;
  const currentProgress = activeBookings % 10;
  const neededForNextDiscount = 10 - currentProgress;
  const progressPercent = Math.min(100, Math.round((currentProgress / 10) * 100));

  return (
    <div className="student-promo-card" dir="rtl">
      {/* Header */}
      <div className="promo-card-header">
        <div>
          <div className="promo-header-badge">
            <Sparkles size={14} />
            <span>برنامج دعوة الزملاء — خصم 10% تراكمي</span>
          </div>
          <h2 className="promo-title">
            <span>ادعُ 10 من زملائك واحصل على خصم 10% على رحلات الشهر القادم!</span>
            <Sparkles size={18} className="text-amber-500" />
          </h2>
          <p className="promo-subtitle">
            شارك كود البرومو الخاص بك مع زملائك في الكلية. عند تسجيل وحجز كل 10 طلاب، ستحصل تلقائياً على خصم 10% لكافة رحلاتك للشهر القادم. وإذا دعوت 10 طلاب إضافيين يُرحل الخصم 10% للشهر التالي مباشرة!
          </p>
        </div>
      </div>

      {/* Main Grid: Code & Progress */}
      <div className="promo-main-row">
        {/* Left: Code Box */}
        <div className="promo-code-box">
          <div className="promo-code-label-group">
            <span className="promo-code-label">كود البرومو الخاص بك</span>
            <span className="promo-code-text" dir="ltr">{referralCode || 'جاري التوليد...'}</span>
          </div>

          <div className="promo-actions-group">
            <button
              type="button"
              className={`btn-promo-copy ${copied ? 'copied' : ''}`}
              onClick={handleCopyCode}
              title="نسخ كود البرومو"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'تم النسخ ✓' : 'نسخ الكود'}</span>
            </button>

            <button
              type="button"
              className="btn-promo-whatsapp"
              onClick={handleShareWhatsApp}
              title="مشاركة الكود عبر الواتساب"
            >
              <Send size={15} />
              <span>مشاركة واتساب</span>
            </button>
          </div>
        </div>

        {/* Right: Progress Tracker */}
        <div className="promo-progress-section">
          <div className="promo-progress-top">
            <span className="promo-progress-title flex items-center gap-1.5">
              <Users size={16} className="text-[#E5232A]" />
              <span>تقدم دعوة الزملاء</span>
            </span>
            <span className="promo-progress-count">
              {currentProgress} / 10 طلاب حجزوا
            </span>
          </div>

          <div className="promo-progress-track">
            <div
              className="promo-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>

          <div className="promo-progress-hint">
            <span>
              {neededForNextDiscount === 10
                ? 'ابدأ بمشاركة كودك لدعوة 10 طلاب وتفعيل خصم الشهر القادم!'
                : `يتبقى ${neededForNextDiscount} طلاب لحصولك على الخصم القادم 10%!`}
            </span>
            <span className="text-slate-400 font-mono">
              (إجمالي المسجلين بكودك: {stats.totalInvited})
            </span>
          </div>
        </div>
      </div>

      {/* Active Earned Discount Months Badges */}
      {stats.earnedDiscountMonths && stats.earnedDiscountMonths.length > 0 && (
        <div className="promo-discounts-badges">
          <span className="text-xs font-bold text-emerald-800 flex items-center gap-1">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span>الشهور المفعل عليها الخصم لك (10%):</span>
          </span>
          {stats.earnedDiscountMonths.map((ym) => (
            <span key={ym} className="promo-discount-tag">
              <Calendar size={14} />
              <span>شهر {formatArabicMonth(ym)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
